import { Errors } from '../enums/errors';
import { KeySystem } from '../enums/key_system';
import { FileError } from '../error/file';
import { FragmentError } from '../error/fragment';
import { Content, PROTECTED_AUDIO_CODEC, UNPROTECTED_AUDIO_CODEC, } from './content';
import { defer } from './defer';
import { createProtectedManifestURL } from './helpers/hls';
const DEFAULT_FRONT_PADDING_DURATION = 1024 / 44100; // samples / samplerate
/**
 * The minimum amount of time between two resolve requests before we get a new
 * URL from the Storage Resolve service.
 */
const MINIMUM_RESOLVE_THRESHOLD = 1001;
const AUDIO_FRAGMENT_LENGTH = 10;
function isValidPsshField(psshField, descriptor) {
    return descriptor.hasOwnProperty(psshField);
}
export class AudioContent extends Content {
    constructor(options) {
        super(options);
        this._fetching = [];
        this._lastResolveTimestamp = 0;
        this._endPaddingDuration = 0;
        this._frontPaddingDuration = 0;
        this._audioCodec = options.audioCodec || PROTECTED_AUDIO_CODEC;
        this._bypassResolve = !!this._resolvedURL;
        this._emitWarning = options.emitWarning || function () { };
        this._mediaType = 'audio';
        this._noManifest = !!options.noManifest;
        this._preloadedManifest = options.preloadedManifest;
        this._resolver = options.resolver;
        this._segmentLength = AUDIO_FRAGMENT_LENGTH;
    }
    static create(options) {
        return new AudioContent(options);
    }
    _calculateFragments(descriptor) {
        // Offset is the start of the first fragment.
        let offset = descriptor.offset;
        // Initialization fragment. Used to initialize audio.
        this._initFragment = {
            init: true,
            cacheBuffer: !this._disableCache,
            timeStart: 0,
            timeEnd: 0,
            audio: {
                // bitrate: 0,
                buffer: null,
                bufferURL: null,
                byteEnd: offset - 1,
                byteStart: 0,
                codec: this._audioCodec,
            },
        };
        const segments = descriptor.segments;
        const timescale = descriptor.timescale;
        const refLength = segments.length;
        const fragments = new Array(refLength);
        let time = 0;
        let totalDuration = 0;
        for (let i = 0, l = refLength; i < l; i++) {
            const segment = segments[i];
            const segmentSize = segment[0];
            const segmentDuration = segment[1];
            const duration = segmentDuration / timescale;
            const bufferData = {
                // bitrate: 0,
                byteStart: offset,
                byteEnd: offset + (segmentSize - 1),
                codec: this._audioCodec,
                buffer: null,
                bufferURL: null,
            };
            const fragment = {
                init: false,
                cacheBuffer: !i && !this._disableCache,
                timeStart: time,
                timeEnd: time + duration,
                audio: bufferData,
            };
            fragments[i] = fragment;
            offset = offset + segmentSize;
            totalDuration += segmentDuration;
            time += duration;
        }
        const byteEnd = fragments[fragments.length - 1].byteEnd;
        fragments[fragments.length - 1].isLastFragment = true;
        this._fragments = fragments;
        this._duration = totalDuration / timescale;
        this._frontPaddingDuration = DEFAULT_FRONT_PADDING_DURATION;
        this._endPaddingDuration = (2048 + (1024 - (byteEnd % 1024))) / timescale;
    }
    _calculateFragmentsV1(descriptor) {
        // Offset is the start of the first fragment.
        let offset = descriptor.offset;
        // Initialization fragment. Used to initialize audio.
        const initBuffer = {
            // bitrate: 0,
            codec: this._audioCodec,
            buffer: null,
            bufferURL: null,
            byteStart: 0,
            byteEnd: offset - 1,
        };
        this._initFragment = {
            init: true,
            cacheBuffer: !this._disableCache,
            timeStart: 0,
            timeEnd: 0,
            audio: initBuffer,
        };
        const references = descriptor.references;
        const timescale = descriptor.timescale;
        const refLength = references.length;
        const fragments = new Array(refLength);
        let totalDuration = 0;
        let time = 0;
        for (let i = 0, l = refLength; i < l; i++) {
            const reference = references[i];
            const duration = reference.duration / timescale;
            const bufferData = {
                byteStart: offset,
                byteEnd: offset + (reference.size - 1),
                // bitrate: 0,
                codec: this._audioCodec,
                buffer: null,
                bufferURL: null,
            };
            const fragment = {
                init: false,
                cacheBuffer: !i && !this._disableCache,
                timeStart: time,
                timeEnd: time + duration,
                audio: bufferData,
            };
            fragments[i] = fragment;
            offset = offset + reference.size;
            totalDuration += reference.duration;
            time += duration;
        }
        fragments[fragments.length - 1].isLastFragment = true;
        this._fragments = fragments;
        this._duration = totalDuration / timescale;
    }
    _endFetching(resolver) {
        const index = this._fetching.indexOf(resolver);
        if (index === -1) {
            return;
        }
        this._fetching.splice(index, 1);
    }
    _getManifest(measures) {
        if (this._noManifest) {
            if (measures) {
                measures.manifestLatency = 0;
            }
            return Promise.resolve(null);
        }
        if (this._preloadedManifest) {
            if (measures) {
                measures.manifestLatency = 0;
            }
            return Promise.resolve(this._preloadedManifest);
        }
        const timeStart = Date.now();
        return this._resolver.getManifest(this._fileId).then(function (descriptor) {
            if (measures) {
                measures.manifestLatency = Date.now() - timeStart;
            }
            return descriptor;
        });
    }
    _startFetching(canceller) {
        this._fetching.push(canceller);
    }
    getHLSURL() {
        if (!this._resolvedURL) {
            throw new FileError(Errors.FILE_NOT_RESOLVED, 'Cannot return HLS Manifest URL: File not resolved.');
        }
        return createProtectedManifestURL({
            assetID: this._fileId,
            // Note that for the manifest, the `EXT-X-TARGETDURATION`, which is the
            // max duration for a segment, must be an integer that's lower or equal
            // to the highest value of an `EXTINF`, which is why we use `Math.floor`.
            // Whereas the `EXTINF` value can be a float post version 3.,
            targetDuration: Math.floor(this._duration),
            segments: [
                {
                    duration: this._duration,
                    url: this._resolvedURL,
                },
            ],
        });
    }
    clearCachedBuffers() {
        if (this._headFragment) {
            delete this._headFragment;
        }
        if (this._initFragment) {
            this._initFragment.audio.buffer = null;
        }
        if (this._fragments && this._fragments[0]) {
            this._fragments[0].audio.buffer = null;
        }
    }
    getPlayableCodecs() {
        return [this.isProtected() ? this._audioCodec : UNPROTECTED_AUDIO_CODEC];
    }
    getFirstFragment() {
        return this._fragments[0];
    }
    getHeadFragment() {
        const initFragment = this._initFragment;
        const firstFragment = this._fragments[0];
        if (!this._headFragment) {
            this._headFragment = {
                init: true,
                cacheBuffer: true,
                timeStart: 0,
                timeEnd: 0,
                audio: {
                    // bitrate: 0,
                    byteStart: initFragment.audio.byteStart,
                    byteEnd: firstFragment.audio.byteEnd,
                    codec: this._audioCodec,
                    bufferURL: null,
                    buffer: null,
                    bandwidth: 0,
                    sliceInto: [
                        {
                            fragment: initFragment,
                            start: 0,
                            end: firstFragment.audio.byteStart,
                        },
                        {
                            fragment: firstFragment,
                            start: firstFragment.audio.byteStart,
                            end: undefined,
                        },
                    ],
                },
            };
        }
        return this._headFragment;
    }
    getFragmentAfterTime(time) {
        if (time === 0 || time === 0.01) {
            return this._fragments[1];
        }
        for (const fragment of this._fragments) {
            if (fragment.timeStart > time) {
                return fragment;
            }
        }
        return null;
    }
    getFragmentForTime(time) {
        if (time === 0 || time === 0.01) {
            return this._fragments[0];
        }
        for (const fragment of this._fragments) {
            if (fragment.timeStart <= time && fragment.timeEnd >= time) {
                return fragment;
            }
        }
        return null;
    }
    // # FIXME: this should probably be factored out to a bunch of functions.
    // possibly one for each response code in the huge switch?
    // keeping it like this during this initial refactoring.
    getBufferForFragment(fragment) {
        if (!fragment) {
            return Promise.reject(new Error('FIXME: error type?'));
        }
        if (fragment.audio && fragment.audio.buffer) {
            return Promise.resolve({ audio: fragment.audio });
        }
        if (!this._resolvedURL) {
            return Promise.reject(new FileError(Errors.FILE_NOT_RESOLVED, 'Cannot fetch buffer: No resolved URL'));
        }
        const canceller = defer();
        this._startFetching(canceller);
        const expectedLength = fragment.audio.byteEnd + 1 - fragment.audio.byteStart;
        const byteRange = `${fragment.audio.byteStart}-${fragment.audio.byteEnd}`;
        const timeRange = `${fragment.timeStart}-${fragment.timeEnd}`;
        let fragmentErrorOnline;
        let buffer;
        let hasFallback; // Used for fallback checking
        const requestURL = this._resolvedURL;
        return this._transport
            .request(requestURL, {
            method: 'GET',
            responseType: 'arraybuffer',
            headers: {
                Range: `bytes=${byteRange}`,
            },
            timing: true,
            canceller: canceller.promise,
            retry: {
                condition: (response, StatusFamily) => {
                    if (response.status === 0) {
                        return response.offline; // retry only if client is offline.
                    }
                    const statusFamily = response.getStatusFamily();
                    if (statusFamily === StatusFamily.CONNECTION_ERROR ||
                        statusFamily === StatusFamily.SERVER_ERROR) {
                        return true;
                    }
                    if (statusFamily === StatusFamily.SUCCESS && response.body) {
                        return response.body.byteLength !== expectedLength;
                    }
                    return false;
                },
            },
        })
            .then((response) => {
            this._endFetching(canceller);
            switch (response.status) {
                case 0:
                    if (response.offline) {
                        return Promise.reject(new FragmentError(Errors.FRAGMENT_OFFLINE_REQUEST_FAILED_WITH_ZERO, 'Request failed with status 0.', 0, {
                            time_range: timeRange,
                            byte_range: byteRange,
                        }));
                    }
                    fragmentErrorOnline = new FragmentError(Errors.FRAGMENT_ONLINE_REQUEST_FAILED_WITH_ZERO, 'Request failed with status 0.', 0);
                    hasFallback = false;
                    if (this._resolvedURL !== requestURL) {
                        // The fallback has been triggered by a parallel request, retry with
                        // the fallback.
                        hasFallback = true;
                    }
                    else if (this._fallbackURLs.length &&
                        this._resolvedURL === requestURL) {
                        // If there are fallback URLs, we should retry. Also, ensure to only
                        // do the retry if it hasn't been done so by any parallel requests.
                        hasFallback = true;
                        this._resolvedURL = this._fallbackURLs.shift();
                    }
                    fragmentErrorOnline.debug.has_fallback = hasFallback;
                    if (hasFallback) {
                        this._emitWarning(fragmentErrorOnline, this.toLogJSON());
                        return this.getBufferForFragment(fragment);
                    }
                    return Promise.reject(fragmentErrorOnline);
                case 200:
                case 206:
                    if (!response.body) {
                        return Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_EMPTY_RESPONSE, 'Empty response for successful buffer.', response.status, {
                            time_range: timeRange,
                            byte_range: byteRange,
                        }));
                    }
                    buffer = response.body;
                    if (buffer.byteLength !== expectedLength) {
                        return Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_UNEXPECTED_LENGTH, 'Received buffer of unexpected length.', response.status, {
                            time_range: timeRange,
                            byte_range: byteRange,
                            received_length: buffer.byteLength,
                            expected_length: expectedLength,
                        }));
                    }
                    if (fragment.cacheBuffer && buffer && buffer.byteLength) {
                        fragment.audio.bufferURL = requestURL;
                        fragment.audio.buffer = buffer;
                        const sliceInto = fragment.audio.sliceInto;
                        if (sliceInto && sliceInto.length) {
                            for (let i = 0, l = sliceInto.length; i < l; i++) {
                                const sliced = sliceInto[i];
                                if (!sliced.fragment || !sliced.fragment.cacheBuffer) {
                                    continue;
                                }
                                sliced.fragment.audio.bufferURL = requestURL;
                                sliced.fragment.audio.buffer = buffer.slice(sliced.start, sliced.end);
                            }
                        }
                    }
                    this._abrManager.sample(buffer.byteLength, response.timing.completed);
                    return Promise.resolve({
                        audio: {
                            // bitrate: fragment.audio.bitrate,
                            bufferURL: requestURL,
                            byteStart: fragment.audio.byteStart,
                            byteEnd: fragment.audio.byteEnd,
                            buffer: buffer,
                            codec: this._audioCodec,
                            bandwidth: (8000 * buffer.byteLength) / response.timing.completed,
                        },
                    });
                case 403:
                    // Track has expired, request new CDN URL. Note that we do not use the
                    // fallback URLs as they were requested at the same time, and are most
                    // likely also expired at this point.
                    return this.resolve().then(() => {
                        return this.getBufferForFragment(fragment);
                    });
                default:
                    hasFallback = false;
                    if (this._resolvedURL !== requestURL) {
                        hasFallback = true;
                    }
                    else if (this._fallbackURLs.length &&
                        this._resolvedURL === requestURL) {
                        hasFallback = true;
                        this._resolvedURL = this._fallbackURLs.shift();
                    }
                    return hasFallback
                        ? this.getBufferForFragment(fragment)
                        : Promise.reject(new FragmentError(Errors.FRAGMENT_REQUEST_FAILED_WITH_STATUS, `Buffer request failed with status ${response.status}`, response.status, {
                            time_range: timeRange,
                            byte_range: byteRange,
                        }));
            }
        });
    }
    resolve(measures) {
        if (this._bypassResolve) {
            if (measures) {
                measures.resolveLatency = 0;
            }
            // If a resolvedURL was passed during the creation of the track, we
            // disregard any calls to resolve().
            return Promise.resolve({
                uri: this._resolvedURL,
                uris: [],
                protection: !!this._preloadedManifest || !this._noManifest ? 'cenc' : undefined,
            });
        }
        clearTimeout(this._lastResolveToken);
        const resolver = () => {
            this._lastResolveTimestamp = Date.now();
            return this._resolver.getCDNURL(this._fileId).then((data) => {
                if (measures) {
                    measures.resolveLatency = Date.now() - this._lastResolveTimestamp;
                }
                return data;
            });
        };
        const elapsedSinceLastResolve = Date.now() - this._lastResolveTimestamp;
        let resolverPromise;
        if (elapsedSinceLastResolve < MINIMUM_RESOLVE_THRESHOLD) {
            resolverPromise = new Promise((resolve, reject) => {
                this._lastResolveToken = setTimeout(() => {
                    resolver().then(resolve, reject);
                }, MINIMUM_RESOLVE_THRESHOLD - elapsedSinceLastResolve);
            });
        }
        else {
            resolverPromise = resolver();
        }
        return resolverPromise
            .then((data) => {
            this._resolvedURLs = [];
            for (let i = 0, len = data.uris.length; i < len; i++) {
                // We're triming these urls from any query params (tokens et al) that
                // are contained in the urls in the response.
                const trimmed = data.uris[i].split('?')[0];
                this._resolvedURLs.push(trimmed);
            }
            this._fallbackURLs = data.uris;
            this._resolvedURL = this._fallbackURLs.shift();
            return data;
        })
            .catch((e) => {
            if (e) {
                e.track = this.toLogJSON();
            }
            return Promise.reject(e);
        });
    }
    load(measures = {
        resolveLatency: 0,
        manifestLatency: 0,
    }) {
        if (this._loaded) {
            measures.resolveLatency = 0;
            measures.manifestLatency = 0;
            return Promise.resolve(this);
        }
        // FIXME: This is required for 3.8 as Promise.all() does not correctly
        // infer the types.
        return Promise.all([
            this.resolve(measures),
            this._getManifest(measures),
        ]).then(([data, descriptor]) => {
            this._loaded = true;
            if (!data.protection || !descriptor) {
                return this;
            }
            // This protected track was loaded on a player that does not
            // support protected tracks. We set these dummy values to
            // ensure that `isProtected()` will still return true, but as this
            // track is not going to be played, these values should not matter.
            if (this._keySystem === KeySystem.INVALID_SPOTIFY_KEY) {
                this._protection = descriptor.protection || 'cenc';
                this._psshBox = new Uint8Array(0);
                return this;
            }
            let error;
            const psshField = this._keySystemSettings.pssh_field.audio;
            let pssh = isValidPsshField(psshField, descriptor)
                ? descriptor[psshField]
                : null;
            if (!pssh && psshField === 'pssh_widevine') {
                pssh = descriptor.pssh;
            }
            if (!pssh) {
                error = new FileError(Errors.FILE_FORMAT_NOT_SUPPORTED, 'KeySystem does not support the file format.');
                error.track = this.toLogJSON();
                return Promise.reject(error);
            }
            this._protection = descriptor.protection || 'cenc';
            this._psshBox = this._parsePSSHBox(pssh);
            // Parse segments
            if (descriptor.segments) {
                this._calculateFragments(descriptor);
            }
            else if (descriptor.references) {
                this._calculateFragmentsV1(descriptor);
            }
            else {
                error = new FileError(Errors.FILE_MALFORMED_SEEKTABLE, 'Malformed seektable.');
                // Add the content info so end-song logging will work.
                error.track = this.toLogJSON();
                return Promise.reject(error);
            }
            return this;
        });
    }
    abort() {
        this._fetching.forEach((resolver) => {
            if (typeof resolver.resolve !== 'function') {
                return;
            }
            resolver.resolve(true);
        });
        this._fetching = [];
    }
}
//# sourceMappingURL=audio_content.js.map